import { PlayCircle } from './PlayCircle';
import { TrackActionsWrapper } from '../Actions/TrackActions';
import { AlbumActionsWrapper } from '../Actions/AlbumActions';
import { ArtistActionsWrapper } from '../Actions/ArtistActions';
import { PlayistActionsWrapper } from '../Actions/PlaylistActions';

// Interfaces
import type { Track } from '../../interfaces/track';
import type { Album } from '../../interfaces/albums';
import type { Artist } from '../../interfaces/artist';
import type { Playlist } from '../../interfaces/playlists';

// Utils
import { useTranslation } from 'react-i18next';

// Redux
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/store';

// Constants
import { PLAYLIST_DEFAULT_IMAGE } from '../../constants/spotify';
import { uiActions } from '../../store/slices/ui';
import { searchHistoryActions } from '../../store/slices/searchHistory';
import { playerService } from '../../services/player';
import { useCallback, useEffect, useState } from 'react';
import logo from '../../logo.png';
import { ReactComponent as PlaylistLogo } from '../../playlist-logo.svg';
import { getImageAnalysis2, getLeastDominantColor } from '../../utils/imageAnyliser';

const Card = ({
  uri,
  title,
  image,
  rounded,
  description,
  onClick,
  context,
  showWatermark,
}: {
  uri: string;
  image: string;
  title: string;
  rounded?: boolean;
  description: string;
  onClick: () => void;
  context: { context_uri?: string; uris?: string[] };
  showWatermark?: boolean;
}) => {
  const paused = useAppSelector((state) => state.spotify.state?.paused);
  const contextUri = useAppSelector((state) => state.spotify.state?.context.uri);
  const isCurrent = contextUri === uri;

  const [logoColor, setLogoColor] = useState<string>('#ffffff');
  useEffect(() => {
    if (showWatermark && image) {
      getLeastDominantColor(image)
        .then((c) => setLogoColor(c || '#ffffff'))
        .catch(() => setLogoColor('#ffffff'));
    }
  }, [image, showWatermark]);

  return (
    <div
      onClick={onClick}
      style={{ cursor: 'pointer' }}
      className='playlist-card relative rounded-lg overflow-hidden  hover:bg-spotify-gray-lightest transition'
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        <img
          src={image}
          alt={title}
          className={rounded ? 'rounded' : ''}
          style={{ borderRadius: 5, width: '100%' }}
        />
        {showWatermark && (
          <div style={{ position: 'absolute', top: 13.5, left: 15, zIndex: 10, width: 24, height: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <PlaylistLogo style={{ color: logoColor, width: 22, height: 22, objectFit: 'contain', transform: 'scale(1.09)' }} />
          </div>
        )}
        <div
          className={`circle-play-div transition translate-y-1/4 ${
            isCurrent && !paused ? 'active' : ''
          }`}
        >
          <PlayCircle image={image} isCurrent={isCurrent} context={context} />
        </div>
      </div>
      <div className='playlist-card-info'>
        <h3 className='text-md font-semibold text-white'>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
};

export const ArtistCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Artist;
  onClick?: () => void;
  getDescription?: (item: Artist) => string;
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation(['artist']);

  const title = item.name;
  const description = getDescription ? getDescription(item) : t('Artist');

  return (
    <ArtistActionsWrapper artist={item} trigger={['contextMenu']}>
      <div onClick={onClick}>
        <Card
          rounded
          title={title}
          uri={item.uri}
          description={description}
          image={item.images[0]?.url}
          context={{ context_uri: item.uri }}
          onClick={() => navigate(`/artist/${item.id}`)}
        />
      </div>
    </ArtistActionsWrapper>
  );
};

export const AlbumCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Album;
  onClick?: () => void;
  getDescription?: (playlist: Album) => string;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const onNavigate = useCallback(() => {
    if (!user) {
      return dispatch(uiActions.openLoginModal(item.images[0].url));
    }
    navigate(`/album/${item.id}`);
  }, [user, navigate, item.id, item.images, dispatch]);

  const title = item.name;

  const description = getDescription
    ? getDescription(item)
    : item.artists
        .slice(0, 3)
        .map((artist) => artist.name)
        .join(', ');

  return (
    <AlbumActionsWrapper album={item} trigger={['contextMenu']}>
      <div onClick={onClick}>
        <Card
          title={title}
          uri={item.uri}
          onClick={onNavigate}
          description={description}
          image={item.images[0]?.url}
          context={{ context_uri: item.uri }}
        />
      </div>
    </AlbumActionsWrapper>
  );
};

export const PlaylistCard = ({
  item,
  onClick,
  getDescription,
}: {
  item: Playlist;
  onClick?: () => void;
  getDescription?: (playlist: Playlist) => string;
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation(['playlist']);

  const title = item.name;
  // Feb 2026 renamed the playlist track-count field `tracks` → `items`; fall back so we never
  // render "undefined songs" regardless of which endpoint the playlist came from.
  const trackTotal = (item as any).tracks?.total ?? (item as any).items?.total;
  const description = getDescription
    ? getDescription(item)
    : trackTotal === undefined
      ? ''
      : trackTotal + ' ' + t(trackTotal === 1 ? 'song' : 'songs');

  const isJioSaavn = item.owner?.display_name === 'JioSaavn User' || item.owner?.id === '0';

  return (
    <PlayistActionsWrapper playlist={item} trigger={['contextMenu']}>
      <div onClick={onClick}>
        <Card
          title={title}
          showWatermark={isJioSaavn}
          uri={item.uri}
          description={description}
          context={{ context_uri: item.uri }}
          onClick={() => navigate(`/playlist/${item.id}`)}
          image={item.images && item.images.length ? item.images[0].url : PLAYLIST_DEFAULT_IMAGE}
        />
      </div>
    </PlayistActionsWrapper>
  );
};

export const TrackCard = ({
  item,
  getDescription,
  onClick,
}: {
  item: Track;
  onClick?: () => void;
  getDescription?: (track: Track) => string;
}) => {
  const dispatch = useAppDispatch();
  const description = getDescription
    ? getDescription(item)
    : item.artists?.map((a) => a.name).join(', ') || item.album?.name || '';

  const handlePlay = () => {
    dispatch(searchHistoryActions.setItem(item));
    if (onClick) {
      onClick();
    } else {
      playerService.startPlayback({ song: item, uris: [item.uri], tracks: [item] });
    }
  };

  return (
    <TrackActionsWrapper track={item} trigger={['contextMenu']}>
      <div onClick={onClick}>
        <Card
          uri={item.uri}
          title={item.name}
          description={description}
          context={{ uris: [item.uri], tracks: [item], song: item } as any}
          image={item.album?.images?.[0]?.url}
          onClick={handlePlay}
        />
      </div>
    </TrackActionsWrapper>
  );
};

export const RadioCard = ({
  item,
  onClick,
}: {
  item: any; // RadioStation
  onClick?: () => void;
}) => {
  const navigate = useNavigate();
  const paused = useAppSelector((state) => state.spotify.state?.paused);
  const contextUri = useAppSelector((state) => state.spotify.state?.context.uri);
  const isCurrent = contextUri === item.uri;

  const COLORS = ['#FF4632', '#1DB954', '#1E3264', '#E8115B', '#8C1932', '#509BF5', '#B49BC8', '#F59B23'];
  const bgColor = COLORS[item.id.charCodeAt(0) % COLORS.length];

  return (
    <div
      onClick={() => {
        if (onClick) onClick();
        navigate(`/radio/${item.id}`);
      }}
      style={{ cursor: 'pointer' }}
      className='playlist-card relative rounded-lg overflow-hidden hover:bg-spotify-gray-lightest transition group'
    >
      <div
        style={{ position: 'relative' }}
        className='aspect-square md:aspect-w-1 md:aspect-h-1/2 lg:aspect-w-1 lg:aspect-h-3/4 xl:aspect-w-1 xl:aspect-h-4/5 p-4'
      >
        <div
          style={{ position: 'relative', backgroundColor: bgColor, width: '100%', height: '100%', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
          className='overflow-hidden'
        >
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div style={{ position: 'absolute', top: '16px', right: '16px', fontSize: '12px', fontWeight: 800, letterSpacing: '1px', color: '#fff', opacity: 0.8 }}>
              RADIO
            </div>
            <div style={{ position: 'absolute', top: '16px', left: '16px', color: '#fff', opacity: 0.8 }}>
              <svg role="img" height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.44-.48.18-1.02-.06-1.14-.54-.12-.48.06-1.02.54-1.14 4.26-1.26 9.715-.6 13.5 1.74.48.24.6.84.301 1.08zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.6.18-1.2.72-1.38 4.26-1.32 11.28-1.02 15.721 1.62.539.3.719 1.02.42 1.56-.299.48-1.02.6-1.56.3z" />
              </svg>
            </div>
            
          <div style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}>            {item.images[1] ? (
              <img
                src={item.images[1]}
                style={{ position: 'absolute', left: '10%', top: '50%', transform: 'translateY(-50%)', width: '24%', height: '24%', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', objectFit: 'cover' }}
                alt=""
              />
            ) : (
              <div
                style={{ position: 'absolute', left: '10%', top: '50%', transform: 'translateY(-50%)', width: '24%', height: '24%', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.15)' }}
              />
            )}
            
            {item.images[2] ? (
              <img
                src={item.images[2]}
                style={{ position: 'absolute', right: '10%', top: '50%', transform: 'translateY(-50%)', width: '24%', height: '24%', borderRadius: '50%', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', objectFit: 'cover' }}
                alt=""
              />
            ) : (
              <div
                style={{ position: 'absolute', right: '10%', top: '50%', transform: 'translateY(-50%)', width: '24%', height: '24%', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.15)' }}
              />
            )}
            
            <img
              src={item.images[0]}
              style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '36%', height: '36%', borderRadius: '50%', zIndex: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', objectFit: 'cover' }}
              alt={item.name}
            />
          </div>
            
          <div style={{ position: 'absolute', bottom: '16px', left: '16px', right: '16px', zIndex: 11 }}>
            <h3 className='text-white' style={{ 
              fontSize: item.name.length > 30 ? '12px' : item.name.length > 15 ? '14px' : '16px', 
              fontWeight: 800,
              letterSpacing: '-0.5px', 
              lineHeight: '1.2',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              margin: 0
            }}>{item.name}</h3>
          </div>
        </div>
        <div
          className={`circle-play-div transition translate-y-1/4 ${
            isCurrent && !paused ? 'active' : ''
          }`}
          style={{ zIndex: 20 }}
        >
          <PlayCircle image={item.images[0]} isCurrent={isCurrent} context={{ context_uri: item.uri }} />
        </div>
      </div>
      <div className='playlist-card-info' style={{ paddingLeft: '16px', paddingRight: '16px', paddingTop: '8px' }}>
        <p style={{ color: '#a7a7a7', fontSize: '14px', fontWeight: 500 }} className="line-clamp-2">{item.description}</p>
      </div>
    </div>
  </div>
);
};
